import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { formatDayTime, withCountRu } from '@dorixona/utils'
import { Icon } from '../../ui/Icon'
import { Failed, failureText } from '../../ui/Load'
import { TableSkeleton } from '../../ui/Skeleton'
import { Pagination } from '../../ui/Pagination'
import { PageActions } from '../../app/PageHeader'
import { useMarkRead, useNotifications, useUnreadCount } from './api'
import { notificationHref, templateLook, type StaffNotification } from './model'
import { PushCard } from './PushCard'
import { Modal } from '../../ui/Modal'

export interface NotificationsScreenProps {
  onToast: (message: string) => void
}

const PAGE_SIZES = [10, 25, 50] as const

function pageFrom(value: string | null): number {
  const page = Number(value)
  return Number.isInteger(page) && page > 0 ? page : 1
}

function sizeFrom(value: string | null): number {
  const size = Number(value)
  return PAGE_SIZES.find((s) => s === size) ?? PAGE_SIZES[0]
}

const stay = (event: { stopPropagation: () => void }) => {
  event.stopPropagation()
}

/**
 * Уведомления сотрудника, новые сверху: новые заказы аптеки, переданные в неё
 * и те, что никто не может взять. Нажатие отмечает прочитанным и открывает
 * заказ. Фильтр и страница — в адресе.
 */
export function NotificationsScreen({ onToast }: NotificationsScreenProps) {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const [settings, setSettings] = useState(false)
  const unreadOnly = params.get('unread') === '1'
  const page = pageFrom(params.get('page'))
  const pageSize = sizeFrom(params.get('size'))
  const list = useNotifications({ page, pageSize, unreadOnly })
  const unread = useUnreadCount(true)
  const read = useMarkRead()
  const unreadCount = unread.data ?? list.data?.unread ?? 0

  function set(next: { unread?: boolean; page?: number; size?: number }) {
    const merged = { unread: unreadOnly, page: 1, size: pageSize, ...next }
    const query = new URLSearchParams()
    if (merged.unread) query.set('unread', '1')
    if (merged.page > 1) query.set('page', String(merged.page))
    if (merged.size !== PAGE_SIZES[0]) query.set('size', String(merged.size))
    setParams(query, { replace: next.page === undefined })
  }

  function markRead(ids: number[]) {
    read.mutate(ids, {
      onError: (error) => {
        onToast(failureText(error))
      },
    })
  }

  function open(notification: StaffNotification) {
    if (!notification.read) markRead([notification.id])
    const href = notificationHref(notification)
    if (href) void navigate(href)
  }

  return (
    <>
      <PageActions>
        <span
          className="ui-count adm-count"
          aria-label={withCountRu(unreadCount, ['непрочитанное', 'непрочитанных', 'непрочитанных'])}
        >
          {unreadCount}
        </span>
      </PageActions>

      <div className="ui-toolbar nt-toolbar">
        <button
          type="button"
          className="ui-chip"
          aria-pressed={unreadOnly}
          onClick={() => {
            set({ unread: !unreadOnly })
          }}
        >
          {unreadOnly ? <Icon name="i-check" /> : null}
          Только непрочитанные
        </button>
        <span className="spacer" />
        <button
          type="button"
          className="ui-btn"
          data-variant="secondary"
          disabled={unreadCount === 0 || read.isPending}
          onClick={() => {
            markRead([])
          }}
        >
          <Icon name="i-check" />
          Прочитать все
        </button>
        {/* Push и проверка нужны редко — в окне, а не под списком. */}
        <button
          type="button"
          className="ui-btn nt-gear"
          data-variant="secondary"
          aria-label="Настройки уведомлений"
          data-tip="Настройки уведомлений"
          data-tip-pos="bottom"
          data-tip-align="end"
          onClick={() => {
            setSettings(true)
          }}
        >
          <Icon name="i-gear" />
        </button>
      </div>

      <section className="ui-table-card" aria-label="Уведомления">
        {list.isPending ? <TableSkeleton what="уведомления" /> : null}
        {list.isError ? (
          <Failed
            what="уведомления"
            error={list.error}
            onRetry={() => {
              void list.refetch()
            }}
          />
        ) : null}

        {list.data ? (
          <>
            {list.data.items.length === 0 ? (
              <div className="ui-empty">
                <Icon name="i-bell" />
                <b>{unreadOnly ? 'Непрочитанных нет' : 'Уведомлений пока нет'}</b>
                {unreadOnly ? (
                  <button
                    type="button"
                    className="ui-btn"
                    data-size="sm"
                    data-variant="secondary"
                    onClick={() => {
                      set({ unread: false })
                    }}
                  >
                    Показать все
                  </button>
                ) : null}
              </div>
            ) : (
              <div className="ui-table-wrap">
                <table className="ui-table nt-table">
                  <colgroup>
                    <col />
                    <col className="w-date" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th scope="col">Уведомление</th>
                      <th scope="col">Когда</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.data.items.map((n) => {
                      const look = templateLook(n.template)
                      const href = notificationHref(n)
                      return (
                        <tr
                          key={n.id}
                          data-unread={n.read ? undefined : ''}
                          data-href={href ?? undefined}
                          onClick={() => {
                            open(n)
                          }}
                        >
                          <td>
                            <div className="nt-item">
                              <span className="nt-icon" data-tone={look.tone} title={look.label}>
                                <Icon name={look.icon} />
                                <span className="visually-hidden">{look.label}</span>
                              </span>
                              <div className="txt">
                                <button
                                  type="button"
                                  className="primary nt-open"
                                  onClick={(event) => {
                                    stay(event)
                                    open(n)
                                  }}
                                >
                                  {n.read ? null : <span className="nt-dot" aria-hidden="true" />}
                                  {n.title}
                                  {n.read ? null : (
                                    <span className="visually-hidden"> · не прочитано</span>
                                  )}
                                </button>
                                {n.body ? <span className="secondary">{n.body}</span> : null}
                              </div>
                            </div>
                          </td>
                          <td className="nt-when">{n.at ? formatDayTime(n.at) : '—'}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <Pagination
              label="Страницы уведомлений"
              page={page}
              pageSize={pageSize}
              total={list.data.total}
              exact={list.data.exact}
              sizes={PAGE_SIZES}
              busy={list.isPlaceholderData}
              onPage={(next) => {
                set({ page: next })
                window.scrollTo({ top: 0 })
              }}
              onPageSize={(size) => {
                set({ size })
              }}
            />
          </>
        ) : null}
      </section>

      {settings ? (
        <Modal
          title="Настройки уведомлений"
          onClose={() => {
            setSettings(false)
          }}
        >
          <PushCard onToast={onToast} />
          <div className="mfoot">
            <button
              type="button"
              className="ui-btn"
              onClick={() => {
                setSettings(false)
              }}
            >
              Готово
            </button>
          </div>
        </Modal>
      ) : null}
    </>
  )
}
